import React from 'react';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {BrowserRouter, Routes, Route} from 'react-router-dom';
import {ThemeProvider, registerCoreLimits, useAccountStore} from '@nimbitlabs/features';
import {AuthProvider, MagicLinkLogin, Signup, ProtectedRoute, useAuth, CreateAccountPage} from '@nimbitlabs/auth';
import {Toaster, TooltipProvider} from '@nimbitlabs/ui';
import {AppLayout} from './layouts/AppLayout';
import {userRoutes} from '@nimbitlabs/features/users/routes';
import {auditRoutes} from '@nimbitlabs/features/audit/routes';
import {settingsRoutes} from '@nimbitlabs/features/settings/routes';
import {accountRoutes} from '@nimbitlabs/features/account/routes';
import {utilityRoutes} from './pages/routes';
import {NotFound} from './pages/NotFound';
import type {NavGroup} from './AppSidebar';

// Register core limits immediately on module load (before any renders)
registerCoreLimits();

export interface AppRoute {
    path: string;
    element: React.ReactNode;
    protected?: boolean;
    adminOnly?: boolean;
    permission?: string;
    permissions?: string[];
    requireAllPermissions?: boolean;
    requirePaidPlan?: boolean;
}

export interface AppConfig {
    appName: string;
    logo?: React.ReactNode;
    appNavItems: NavGroup[];
    routes: AppRoute[];
    publicRoutes?: AppRoute[];
    landingPage?: React.ReactNode;
    showManagementItems?: boolean;
    queryClient?: QueryClient;
    apiBaseUrl: string;
    authConfig?: {
        loginPath?: string;
        signupPath?: string;
        homePath?: string;
        createAccountPath?: string;
    };
    /**
     * Optional content ribbon that appears at the top of the main content area.
     * Can be used for announcements, onboarding, gamification, banners, etc.
     */
    contentRibbon?: React.ReactNode;
    /**
     * Optional data provider that wraps the app and fetches data on account change
     * Should be a component that accepts children
     */
    dataProvider?: React.ComponentType<{ children: React.ReactNode }>;
}

const defaultQueryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
    },
});

// Default shared routes (automatically included)
const defaultSharedRoutes: AppRoute[] = [
    ...userRoutes,
    ...auditRoutes,
    ...settingsRoutes,
    ...accountRoutes,
    ...utilityRoutes,
];

// Inner component that has access to auth context
function AppRoutes({
                       appName,
                       logo,
                       appNavItems,
                       routes,
                       publicRoutes = [],
                       landingPage,
                       showManagementItems = true,
                       apiBaseUrl,
                       authConfig = {},
                       contentRibbon,
                       dataProvider: DataProvider,
                   }: Omit<AppConfig, 'queryClient'>) {
    const {user} = useAuth();
    const {
        loginPath = '/login',
        signupPath = '/signup',
        homePath = '/dashboard',
        createAccountPath = '/create-account',
    } = authConfig;

    // Sync AuthContext user to AccountStore
    // This triggers all store subscriptions to fetch/clear data
    React.useEffect(() => {
        if (user?.accountId) {
            useAccountStore.getState().setCurrentAccount({
                id: user.accountId,
                accountId: user.accountId,
                companyName: user.companyName || '',
                role: user.role || '',
            } as any);
        } else {
            useAccountStore.getState().setCurrentAccount(null);
        }
    }, [user?.accountId, user?.companyName, user?.role]);

    // Combine app-specific routes with default shared routes
    // App routes come first so they can override shared routes if needed
    const allRoutes = showManagementItems
        ? [...routes, ...defaultSharedRoutes]
        : routes;

    return (
        <BrowserRouter>
            <Routes>
                {/* Landing page */}
                {landingPage && (
                    <Route path="/" element={landingPage}/>
                )}

                {/* Auth routes */}
                <Route
                    path={loginPath}
                    element={<MagicLinkLogin/>}
                />
                <Route
                    path={signupPath}
                    element={<Signup appName={appName} apiBaseUrl={apiBaseUrl}/>}
                />

                {/* Create Account route - automatically included */}
                <Route
                    path={createAccountPath}
                    element={<CreateAccountPage successPath={homePath}/>}
                />

                {/* Public routes */}
                {publicRoutes.map((route) => (
                    <Route
                        key={route.path}
                        path={route.path}
                        element={route.element}
                    />
                ))}

                {/* Protected routes with layout */}
                <Route
                    path="/*"
                    element={
                        <ProtectedRoute requireAccount={true} accountCheckPath="/create-account">
                            {DataProvider ? (
                                <DataProvider>
                                    <AppLayout
                                            appName={appName}
                                            logo={logo}
                                            appNavItems={appNavItems}
                                            showManagementItems={showManagementItems}
                                            apiBaseUrl={apiBaseUrl}
                                            contentRibbon={contentRibbon}
                                        >
                                            <Routes>
                                                {allRoutes.map((route) => (
                                                    <Route
                                                        key={route.path}
                                                        path={route.path}
                                                        element={route.element}
                                                    />
                                                ))}
                                                <Route path="*" element={<NotFound/>}/>
                                            </Routes>
                                        </AppLayout>
                                    </DataProvider>
                                ) : (
                                    <AppLayout
                                        appName={appName}
                                        logo={logo}
                                        appNavItems={appNavItems}
                                        showManagementItems={showManagementItems}
                                        apiBaseUrl={apiBaseUrl}
                                        contentRibbon={contentRibbon}
                                    >
                                        <Routes>
                                            {allRoutes.map((route) => (
                                                <Route
                                                    key={route.path}
                                                    path={route.path}
                                                    element={route.element}
                                                />
                                            ))}
                                            <Route path="*" element={<NotFound/>}/>
                                        </Routes>
                                    </AppLayout>
                                )}
                        </ProtectedRoute>
                    }
                />
            </Routes>
        </BrowserRouter>
    );
}

export function App({
                        appName,
                        logo,
                        appNavItems,
                        routes,
                        publicRoutes = [],
                        landingPage,
                        showManagementItems = true,
                        queryClient = defaultQueryClient,
                        apiBaseUrl,
                        authConfig = {},
                        contentRibbon,
                        dataProvider,
                        useUsersStore,
                        useTeamsStore,
                    }: AppConfig) {
    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider>
                <AuthProvider config={{apiBaseUrl}}>
                    <TooltipProvider>
                        <Toaster/>
                        <AppRoutes
                            appName={appName}
                            logo={logo}
                            appNavItems={appNavItems}
                            routes={routes}
                            publicRoutes={publicRoutes}
                            landingPage={landingPage}
                            showManagementItems={showManagementItems}
                            apiBaseUrl={apiBaseUrl}
                            authConfig={authConfig}
                            contentRibbon={contentRibbon}
                            dataProvider={dataProvider}
                            useUsersStore={useUsersStore}
                            useTeamsStore={useTeamsStore}
                        />
                    </TooltipProvider>
                </AuthProvider>
            </ThemeProvider>
        </QueryClientProvider>
    );
}
